// OrderSummary.jsx
import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

export default function OrderSummary({
	itens = [],
	titulo = "Resumo do pedido",
	garantiaTexto = "Garantia de reembolso em 7 dias",
	minEnabled = 1,
}) {
	// Converte para R$ (já que os valores estão em reais, não centavos)
	const toBRL = (valor) =>
		Number(valor || 0).toLocaleString("pt-BR", {
			style: "currency",
			currency: "BRL",
		});

	const [enabledMap, setEnabledMap] = useState({});

	const resumo = useMemo(() => {
		const rows = itens.map((r) => ({
			...r,
			enabled: enabledMap[r.id_empresa_itens] !== false,
		}));

		const enabledRows = rows.filter((r) => r.enabled);

		// Totais gerais vindos do backend
		const totalDescontos = itens[0]?.total_desconto || 0;
		const totalAcrescimos = itens[0]?.total_acrescimo || 0;
		const total = itens[0]?.total_a_pagar || 0;

		return { rows, enabledRows, totalDescontos, totalAcrescimos, total };
	}, [itens, enabledMap]);

	const enabledCount = resumo.enabledRows.length;

	return (
		<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
			<div className="flex items-center justify-between">
				<h3 className="text-lg font-semibold">{titulo}</h3>
			</div>

			{resumo.rows.length > 0 ? (
				<>
					<ul className="mt-3 divide-y divide-gray-200 border-b">
						{resumo.rows.map((it) => (
							<li key={it.id_empresa_itens} className={`py-2 ${it.enabled ? "" : "opacity-60"}`}>
								<div className="flex flex-col gap-1">
									{/* Nome e valor original */}
									<div className="flex justify-between">
										<span className="font-medium">{it.nome}</span>
										<span className="font-semibold">{toBRL(it.valor)}</span>
									</div>

									{/* Detalhes do item */}
									<div className="text-xs text-gray-500 flex justify-between pl-2">
										<span>Desconto: -{toBRL(it.valor_desconto)}</span>
										<span>Acréscimo: +{toBRL(it.valor_acrescimo)}</span>
										<span>Total: {toBRL(it.valor_pedido)}</span>
									</div>
								</div>
							</li>
						))}
					</ul>

					{/* Totais gerais */}
					<div className="mt-3 space-y-1 text-sm">
						<div className="flex justify-between">
							<span>Subtotal</span>
							<span>{toBRL(resumo.rows.reduce((acc, r) => acc + r.valor, 0))}</span>
						</div>
						<div className="flex justify-between">
							<span>Descontos</span>
							<span>- {toBRL(resumo.totalDescontos)}</span>
						</div>
						<div className="flex justify-between">
							<span>Acréscimos</span>
							<span>+ {toBRL(resumo.totalAcrescimos)}</span>
						</div>
						<div className="flex justify-between border-t pt-2 text-base">
							<span className="font-semibold">Total</span>
							<span className="font-bold">{toBRL(resumo.total)}</span>
						</div>

						{enabledCount <= minEnabled && (
							<div className="text-xs text-gray-500">Mínimo de {minEnabled} item selecionado.</div>
						)}
					</div>

					{/* Garantia */}
					<div className="mt-3 flex items-center gap-2 text-emerald-700 text-sm">
						<FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />
						<span>{garantiaTexto}</span>
					</div>
				</>
			) : (
				<p className="mt-3 text-sm text-gray-500">Nenhum item selecionado.</p>
			)}
		</div>
	);
}
